import _, {chain} from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {reportToBugsnag} from '../../../../../../srcCommon/services/bugsnag/bugsnag';
import {calendarVersionCanReply} from '../../../../../helpers/models/calendarVersionHelpers';
import {buildChannelSettingsPath} from '../../../../../helpers/models/channelHelpers';
import {ResolveDispatchProps, wrapActionCreator} from '../../../../../helpers/reduxHelpers';
import {
  AppParametricSelector,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../helpers/reselectHelpers';
import {
  AttachmentStatusesEnum,
  AttachmentVisualContentTypesEnum,
} from '../../../../../models/api/attachmentModel';
import {CalendarEventVersionResponse} from '../../../../../models/api/calendarEventVersionModel';
import {ChannelModel} from '../../../../../models/api/channelModel';
import {MessageModel} from '../../../../../models/api/messageModel';
import {VisualParticipantModel} from '../../../../../models/api/visualParticipantModel';
import {downloadAttachmentAsync} from '../../../../../services/browser';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../services/tracking';
import {AppState} from '../../../../../store/appState';
import {Dispatch} from '../../../../../store/appStore';
import {replyToCalendarEventVersionAction} from '../../../../../store/data/calendarEventVersions/calendarEventVersionsActions';
import {
  makeGetChannelInbox,
  makeGetReplyChannels,
} from '../../../../../store/data/channels/channelsSelectors';
import {makeGetVisualParticipants} from '../../../../../store/data/contacts/participantsSelectors';
import {
  getCalendarEvents,
  getCalendarEventVersions,
  getCalendarVersions,
} from '../../../../../store/data/dataSelectors';
import {getTimeFormat} from '../../../../../store/data/preferences/preferencesSelectors';
import {getHasNewInboxSettingsDetails} from '../../../../../store/featureFlags/featureFlagsSelectors';
import {navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {CalendarInviteViewer} from './calendarInviteViewer';

/*
 * Props.
 */

interface CalendarInviteViewerContainerProps {
  namespace: string;
  message: MessageModel;
  calendarEventUid: string;
}

/*
 * Selectors.
 */

function getNamespace(_state: AppState, props: CalendarInviteViewerContainerProps) {
  return props.namespace;
}

function getMessage(_state: AppState, props: CalendarInviteViewerContainerProps) {
  return props.message;
}

function getCalendarEventUid(_state: AppState, props: CalendarInviteViewerContainerProps) {
  return props.calendarEventUid;
}

function makeGetCalendarEvent() {
  return createSelector([getCalendarEvents, getCalendarEventUid], (calendarEvents, calendarEventUid) => {
    const calendarEvent = calendarEvents[calendarEventUid];
    if (!calendarEvent) {
      throw new Error(`A calendar event could not be found for id: ${calendarEventUid}`);
    }

    return calendarEvent;
  });
}

function makeGetCalendarEventVersionsForEvent(getCalendarEvent: ReturnType<typeof makeGetCalendarEvent>) {
  return createSelector(
    [getCalendarEvent, getCalendarEventVersions],
    (calendarEvent, calendarEventVersions) => {
      const versions = _(calendarEvent.calendarEventVersionUids)
        .map((uid) => calendarEventVersions[uid])
        .compact()
        .value();

      if (!versions || versions.length === 0) {
        throw new Error('No calendar event versions found.');
      }

      return versions;
    },
  );
}

function makeGetMessageICSAttachments() {
  return createSelector(getMessage, (message) =>
    chain(message.attachments)
      .filter((a) => !a.isInline && a.visualContentType === AttachmentVisualContentTypesEnum.CALENDAR)
      .map((a) => a.status === AttachmentStatusesEnum.UPLOADED && a)
      .compact()
      .value(),
  );
}

// If this messsage has a calendar event, we have to find the calendarEventVersion (if any)
// that would allow the user to update their response status. We use the following steps to determine
// the "reply-able" calendarEventVersion and build responseData:
// 1. Retrieve all available calendarEventVersions
// 2. For each calendarEventVersion:
//    - If the calendarEventVersion belongs to a calendar, ensure that we have a calendarVersion with owner access.
//    - If the calendarEventVersion belongs to a channel, ensure that the channel is a member of replyChannels.
//    - If the calendarEventVersion has a response property, return the complete response data.
//    - Otherwise, attempt to find a matching visual participant and return the response data.
type GetVisualParticipants = AppParametricSelector<any, ReadonlyArray<VisualParticipantModel>>;
function makeGetResponseData(
  getVisualParticipants: GetVisualParticipants,
  getCalendarEventVersionsForEvent: ReturnType<typeof makeGetCalendarEventVersionsForEvent>,
  getReplyChannels: AppParametricSelector<any, ReadonlyArray<ChannelModel>>,
) {
  return createSelector(
    [getVisualParticipants, getCalendarEventVersionsForEvent, getReplyChannels, getCalendarVersions],
    (visualParticipants, calendarEventVersionsForEvent, replyChannels, calendarVersions) => {
      const responses = chain(calendarEventVersionsForEvent)
        .map((calendarEventVersion) => {
          const {calendarVersionIds, response} = calendarEventVersion;

          const replyCalendarVersion = calendarVersionIds
            .map((id) => calendarVersions[id])
            .find((v) => v && calendarVersionCanReply(v, replyChannels));

          // If this calendarEventVersion belongs to a calendar but we dont have
          // a matching calendarVersion, we cant reply to this calendarEventVersion.
          if (calendarVersionIds.length > 0 && !replyCalendarVersion) {
            return null;
          }

          const {channelId} = replyCalendarVersion || calendarEventVersion;
          const replyChannel = replyChannels.find((c) => c.id === channelId);

          if (!replyChannel) {
            return null;
          }

          if (response) {
            return {
              replyChannel,
              replyCalendarVersion,
              response,
              calendarEventVersion,
            };
          }

          const visualParticipant = visualParticipants.find(
            (vp) => vp.channel === replyChannel && vp.isAttendee,
          );
          if (!visualParticipant) {
            return null;
          }

          // If this is an organizer calendarEventVersion, there's no response.
          const participantResponse = visualParticipant.isOrganizer
            ? undefined
            : {
                address: visualParticipant.contact.handle,
                status: visualParticipant.responseStatus,
                comment: undefined,
              };

          return {
            replyChannel,
            replyCalendarVersion,
            response: participantResponse,
            calendarEventVersion,
          };
        })
        .compact()
        .value();

      const primaryCalendarVersionResponse = responses.find(
        (response) => response.replyCalendarVersion && response.replyCalendarVersion.isPrimary,
      );

      // Prefer the responseData for a primary calendarVersion. Otherwise, return the first one.
      return primaryCalendarVersionResponse || responses[0];
    },
  );
}

function makeGetCalendarEventVersion(
  getCalendarEventVersionsForEvent: ReturnType<typeof makeGetCalendarEventVersionsForEvent>,
  getResponseData: ReturnType<typeof makeGetResponseData>,
) {
  return createSelector(
    [getCalendarEventVersionsForEvent, getResponseData],
    (calendarEventVersions, responseData) => {
      // If we have responseData, use the associated calendarEventVersion.
      // Otherwise, default to the first version.
      const responseCalendarEventVersion = responseData && responseData.calendarEventVersion;
      if (responseCalendarEventVersion) {
        return responseCalendarEventVersion;
      }

      if (calendarEventVersions.length === 0) {
        throw new Error('No calendar event versions found.');
      }

      return calendarEventVersions[0];
    },
  );
}

function makeGetReAuthChannel(
  getCalendarEventVersion: ReturnType<typeof makeGetCalendarEventVersion>,
  getReplyChannels: AppParametricSelector<any, ReadonlyArray<ChannelModel>>,
) {
  return createSelector(
    [getCalendarEventVersion, getReplyChannels],
    (calendarEventVersion, replyChannels) => {
      // If the calendarEventVersion we're displaying points to a channelId or belongs to a calendar,
      // we must have been able to fetch the event from the API, so we don't need to show a reauth prompt.
      if (calendarEventVersion.channelId || calendarEventVersion.calendarVersionIds.length > 0) {
        return undefined;
      }

      // Find a channel that is missing calendar scopes.
      const reAuthChannel = replyChannels.find((c) => c.behaviors.isMissingCalendarScopes);
      return reAuthChannel;
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CalendarInviteViewerContainerProps>()(() => {
  const getCalendarEvent = makeGetCalendarEvent();
  const getCalendarEventVersionsForEvent = makeGetCalendarEventVersionsForEvent(getCalendarEvent);
  const getReplyChannels = makeGetReplyChannels({getMessage});
  const getVisualParticipants = makeGetVisualParticipants(getNamespace, getCalendarEvent);
  const getResponseData = makeGetResponseData(
    getVisualParticipants,
    getCalendarEventVersionsForEvent,
    getReplyChannels,
  );
  const getCalendarEventVersion = makeGetCalendarEventVersion(
    getCalendarEventVersionsForEvent,
    getResponseData,
  );
  const getReAuthChannel = makeGetReAuthChannel(getCalendarEventVersion, getReplyChannels);
  const getReAuthInbox = makeGetChannelInbox(getReAuthChannel);

  return {
    calendarEvent: getCalendarEvent,
    calendarEventVersion: getCalendarEventVersion,
    visualParticipants: getVisualParticipants,
    responseData: getResponseData,
    messageICSAttachments: makeGetMessageICSAttachments(),
    reAuthChannel: getReAuthChannel,
    reAuthInbox: getReAuthInbox,
    timeFormat: getTimeFormat,
    hasNewInboxSettingsDetails: getHasNewInboxSettingsDetails,
  };
});

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    replyToCalendarEventVersionAction: wrapActionCreator(dispatch, replyToCalendarEventVersionAction),
    navigateToLinkAction: wrapActionCreator(dispatch, navigateToLinkAction),
  };
}

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: CalendarInviteViewerContainerProps,
) {
  const {calendarEventVersion, responseData, messageICSAttachments, hasNewInboxSettingsDetails} = stateProps;
  const {reAuthInbox, reAuthChannel} = stateProps;

  // NOTE: since isRecurringSaveTypeRequired is false for CalendarInviteViewer, we don't need to
  // handle shouldShowRecurringPrompt/masterCalendarEventVersion here.
  const updateCalendarEventResponse: HandlerOf<CalendarEventVersionResponse> = (newResponse) => {
    if (
      !responseData ||
      !responseData.response ||
      (newResponse.status === responseData.response.status &&
        newResponse.comment === responseData.response.comment)
    ) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.RESPOND_CALENDAR_INVITE, {
      channel_type: responseData.replyChannel && responseData.replyChannel.type,
      status: newResponse.status,
      has_comment: Boolean(newResponse.comment),
    });

    dispatchProps.replyToCalendarEventVersionAction({
      calendarEventVersionUid: calendarEventVersion.uid,
      response: newResponse,
      channelId: responseData.replyChannel.id,
      calendarVersionId: responseData.replyCalendarVersion && responseData.replyCalendarVersion.id,
    });
  };

  const downloadICSAttachments = async () => {
    try {
      const downloadAttachmentPromises = messageICSAttachments.map((a) => downloadAttachmentAsync(a));
      await Promise.all(downloadAttachmentPromises);
    } catch (error) {
      reportToBugsnag(error);
    }
  };

  const onGoToReAuthChannelSettingsClick = () => {
    if (!reAuthInbox || !reAuthChannel) {
      return;
    }

    const settingsPath = buildChannelSettingsPath(hasNewInboxSettingsDetails, reAuthInbox, reAuthChannel);
    dispatchProps.navigateToLinkAction(settingsPath);
  };

  return {
    ...stateProps,
    ...ownProps,
    hasICSAttachments: messageICSAttachments.length > 0,
    replyChannel: responseData && responseData.replyChannel,
    calendarVersion: responseData && responseData.replyCalendarVersion,
    response: responseData && responseData.response,
    updateCalendarEventResponse,
    downloadICSAttachments,
    onGoToReAuthChannelSettingsClick,
  };
}

export const CalendarInviteViewerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(CalendarInviteViewer);
